import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    IoAddOutline,
    IoCheckmarkOutline,
    IoChevronDownOutline,
    IoCloseOutline,
    IoGridOutline,
    IoListOutline,
    IoPulseOutline,
} from "react-icons/io5";
import { getLocale } from "../../../i18n/config";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";
import { localizedSuffix } from "../../../i18n/localizedService";
import { Button } from "../../../components/ui";
import { buildAppointmentTypeMap } from "./planStageLabels";
import { getStageTypeOptions } from "./stageTypes";
import "./PatientPlanMatrix.css";

/* -------------------------------------------------------------------------- */
/*  Помощники                                                                 */
/* -------------------------------------------------------------------------- */

// Название услуги с приоритетом языка интерфейса (same as PatientPlan)
const pickLocalizedValue = (record, key, language) => {
    const suffix = localizedSuffix(language);
    const primary = record?.[`${key}${suffix}`]?.value;
    if (primary != null && String(primary).trim() !== "") return primary;

    const secondary = record?.[suffix ? key : `${key}_en`]?.value;
    if (secondary != null && String(secondary).trim() !== "") return secondary;

    return "";
};

const appointmentNumber = (appointment) =>
    parseInt(appointment?.appointment_number?.value, 10);

const hasAdmission = (appointment) => {
    const id = appointment?.id_admission?.value;
    return id !== undefined && String(id) !== "0" && String(id) !== "";
};

const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Матричное представление плана лечения:
 * строки — направления (группы), столбцы — приёмы (этапы программы).
 * В ячейке — количество услуг приёма; клик открывает попап со списком услуг.
 */
export default function PatientPlanMatrix({
    groups = [],
    planCountAppointment,
    planId,
    appointmentTypes = [],
    onRequestAddGroup,
    onAddGroup,
    onAddStage,
    onChangeStageType,
    onRemoveStage,
    onAddService,
    onAddCustomService,
    addingStage = false,
}) {
    const { t, i18n } = useTranslation("patient");
    const locale = getLocale(i18n.language);
    const [cell, setCell] = useState(null);
    // Открытое выпадающее меню: { kind: "add" } — добавить приём с типом,
    // { kind: "stage", number } — сменить тип у существующего приёма,
    // { kind: "group" } — добавить направление (группу услуг).
    const [menu, setMenu] = useState(null);
    // Список доступных направлений и признак загрузки — грузим при открытии окна.
    const [groupOptions, setGroupOptions] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    // Ссылка на контейнер меню — после рендера поправляем позицию по факту.
    const menuRef = useRef(null);
    // Меню «Добавить услугу» в окне приёма: из каталога / новая услуга.
    // Такое же меню, как у кнопки «Добавить услугу» на вкладке «Группа».
    const [serviceMenuOpen, setServiceMenuOpen] = useState(false);

    // Закрытие меню по клику вне кнопки (как в табличном виде плана).
    useEffect(() => {
        if (!serviceMenuOpen) return undefined;
        const handleClickOutside = (event) => {
            if (!event.target.closest(".plan-matrix-modal__addservice")) {
                setServiceMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [serviceMenuOpen]);

    // Названия этапов программы (типы приёмов) по номеру приёма.
    const typeMap = useMemo(
        () => buildAppointmentTypeMap(appointmentTypes, i18n.language),
        [appointmentTypes, i18n.language],
    );

    // Типы приёмов (этапы программы) — фиксированный системный список.
    // Из интерфейса его нельзя дополнить или отредактировать: пользователь
    // только выбирает название для приёма.
    const typeOptions = useMemo(
        () => getStageTypeOptions(i18n.language),
        [i18n.language],
    );

    // Схема всегда нумерует столбцы приёмами: режим подписи группы
    // (`column_type` — дни недели подряд / чётные / нечётные) влияет только
    // на раздел «Группа» и здесь намеренно игнорируется.
    const openMenu = (kind, number, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMenu({ kind, number, rect });
    };

    // «+ Добавить группу»: открываем компактное окно со списком доступных
    // направлений и подгружаем список.
    const openGroupMenu = async (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMenu({ kind: "group", rect });
        if (!onRequestAddGroup) return;
        setGroupsLoading(true);
        try {
            const list = await onRequestAddGroup();
            setGroupOptions(Array.isArray(list) ? list : []);
        } finally {
            setGroupsLoading(false);
        }
    };

    const closeMenu = () => setMenu(null);

    // Меню плавающее (портал в body): координаты считаются один раз при
    // открытии, поэтому оно закрывается по Escape, прокрутке страницы и
    // ресайзу. Прокрутка ВНУТРИ меню его не закрывает.
    useEffect(() => {
        if (!menu) return undefined;
        const isInsideMenu = (event) => {
            const target = event?.target;
            return Boolean(
                target &&
                    typeof target.closest === "function" &&
                    target.closest(".plan-matrix__menu"),
            );
        };
        const handleScroll = (event) => {
            if (isInsideMenu(event)) return;
            setMenu(null);
        };
        const handleResize = () => setMenu(null);
        const handleKey = (event) => {
            if (event.key === "Escape") setMenu(null);
        };
        window.addEventListener("wheel", handleScroll, { passive: true });
        window.addEventListener("touchmove", handleScroll, { passive: true });
        window.addEventListener("resize", handleResize);
        document.addEventListener("keydown", handleKey);
        return () => {
            window.removeEventListener("wheel", handleScroll);
            window.removeEventListener("touchmove", handleScroll);
            window.removeEventListener("resize", handleResize);
            document.removeEventListener("keydown", handleKey);
        };
    }, [menu]);

    // Меню открывается ВНИЗ от кнопки. Фактическая высота известна только
    // после рендера, поэтому здесь сбрасываем top к «под кнопкой» и, если
    // меню не влезает в экран, сдвигаем вверх ровно на величину переполнения
    // (зазора между кнопкой и меню не появляется).
    useEffect(() => {
        if (!menu) return;
        const el = menuRef.current;
        if (!el) return;
        const gutter = 12;
        el.style.top = `${menu.rect.bottom + 8}px`;
        const rect = el.getBoundingClientRect();
        const overflow = rect.bottom - (window.innerHeight - gutter);
        if (overflow > 0) {
            el.style.top = `${Math.max(gutter, rect.top - overflow)}px`;
        }
    }, [menu, groupOptions, groupsLoading]);

    const renderTypeMenu = () => {
        if (!menu) return null;
        const isAdd = menu.kind === "add";
        const isGroup = menu.kind === "group";
        const viewportWidth =
            typeof window !== "undefined" ? window.innerWidth : 1280;
        const viewportHeight =
            typeof window !== "undefined" ? window.innerHeight : 800;
        const width = 248;
        const gutter = 12;
        const left = Math.min(
            Math.max(gutter, menu.rect.right - width),
            Math.max(gutter, viewportWidth - width - gutter),
        );
        // Позиционирование. Меню ВСЕГДА раскрывается вниз от кнопки (как было
        // изначально): top = низ кнопки + 8px, высота ограничена экраном.
        // Реальная высота известна только после рендера, поэтому если меню не
        // влезает снизу — его минимально сдвигают вверх в useEffect ниже.
        const maxHeight = Math.max(160, viewportHeight - 2 * gutter);
        const style = {
            left,
            width,
            maxHeight,
            top: menu.rect.bottom + 8,
        };
        const activeLabel = isAdd ? "" : typeMap[menu.number] || "";

        return createPortal(
            <>
                <div
                    className="plan-matrix__menu-backdrop"
                    onMouseDown={closeMenu}
                />
                <div
                    className="plan-matrix__menu"
                    role="menu"
                    style={style}
                    ref={menuRef}>
                    <div className="plan-matrix__menu-title">
                        {isGroup
                            ? t("plan.matrix.groupMenuTitle")
                            : isAdd
                              ? t("plan.matrix.typeMenuAddTitle")
                              : t("plan.matrix.typeMenuEditTitle")}
                    </div>
                    {isGroup ? (
                        groupsLoading ? (
                            <div className="plan-matrix__menu-empty">
                                {t("plan.matrix.groupMenuLoading")}
                            </div>
                        ) : groupOptions.length ? (
                            groupOptions.map((option) => (
                                <button
                                    key={`group-${option.id}`}
                                    type="button"
                                    role="menuitem"
                                    className="plan-matrix__menu-item"
                                    onClick={() => {
                                        closeMenu();
                                        onAddGroup?.(option);
                                    }}>
                                    <IoPulseOutline
                                        size={15}
                                        aria-hidden="true"
                                    />
                                    <span>{getGroupTypeLabel(t, option)}</span>
                                </button>
                            ))
                        ) : (
                            <div className="plan-matrix__menu-empty">
                                {t("plan.matrix.groupMenuEmpty")}
                            </div>
                        )
                    ) : null}
                    {isGroup ? null : typeOptions.map((option, index) => {
                        const isActive = !isAdd && option.label === activeLabel;
                        return (
                            <button
                                key={`type-${index}-${option.label}`}
                                type="button"
                                role="menuitem"
                                className={`plan-matrix__menu-item${
                                    isActive ? " is-active" : ""
                                }`}
                                onClick={() => {
                                    closeMenu();
                                    if (isAdd) {
                                        onAddStage(option.name, option.nameEn);
                                    } else {
                                        onChangeStageType(
                                            menu.number,
                                            option.name,
                                            option.nameEn,
                                        );
                                    }
                                }}>
                                <IoPulseOutline size={15} aria-hidden="true" />
                                <span>{option.label}</span>
                                {isActive ? (
                                    <IoCheckmarkOutline
                                        size={15}
                                        className="plan-matrix__menu-check"
                                        aria-hidden="true"
                                    />
                                ) : null}
                            </button>
                        );
                    })}
                    {isGroup ? null : isAdd ? (
                        <button
                            type="button"
                            role="menuitem"
                            className="plan-matrix__menu-item is-reset"
                            onClick={() => {
                                closeMenu();
                                onAddStage();
                            }}>
                            <IoAddOutline size={15} aria-hidden="true" />
                            <span>{t("plan.matrix.addStageNoType")}</span>
                        </button>
                    ) : (
                        <button
                            type="button"
                            role="menuitem"
                            className="plan-matrix__menu-item is-reset"
                            onClick={() => {
                                closeMenu();
                                onChangeStageType(menu.number, "", "");
                            }}>
                            <IoCloseOutline size={15} aria-hidden="true" />
                            <span>{t("plan.matrix.typeMenuReset")}</span>
                        </button>
                    )}
                </div>
            </>,
            document.body,
        );
    };

    // Подпись этапа: своё название из типа приёма или стандартное «Приём N».
    const stageLabel = (number) =>
        typeMap[number] || t("plan.matrix.stage", { number });

    const displayName = (record) =>
        pickLocalizedValue(record, "name", i18n.language);

    // Количество столбцов: максимум из лимита группы, лимита плана и номеров приёмов
    const stages = useMemo(() => {
        let max = 0;
        groups.forEach((group) => {
            const groupLimit = Number(group?.count_appointment?.value) || 0;
            if (groupLimit > max) max = groupLimit;
            (group?.items || []).forEach((item) => {
                (item?.appointments || []).forEach((apt) => {
                    const number = appointmentNumber(apt);
                    if (Number.isFinite(number) && number > max) max = number;
                });
            });
        });
        if (!max && Number(planCountAppointment) > 0) {
            max = Number(planCountAppointment);
        }
        return Array.from({ length: max }, (_, index) => index + 1);
    }, [groups, planCountAppointment]);

    // Услуги группы, попадающие в конкретный приём
    const getCellItems = (group, number) =>
        (group?.items || []).filter((item) =>
            (item?.appointments || []).some(
                (apt) => appointmentNumber(apt) === number,
            ),
        );

    // Текущий этап — максимальный приём, в котором уже есть назначения
    const currentStage = useMemo(() => {
        let current = 0;
        groups.forEach((group) => {
            (group?.items || []).forEach((item) => {
                (item?.appointments || []).forEach((apt) => {
                    const number = appointmentNumber(apt);
                    if (
                        hasAdmission(apt) &&
                        Number.isFinite(number) &&
                        number > current
                    ) {
                        current = number;
                    }
                });
            });
        });
        return current;
    }, [groups]);

    // Состояние столбца (этапа): пройден / текущий / будущий.
    // Определяет тон полосы под столбцом и оформление ячеек.
    const stageState = (number) => {
        if (!currentStage) return "future";
        if (number < currentStage) return "past";
        return number === currentStage ? "current" : "future";
    };

    // Две независимые подписи шапки столбца: сверху крупно — номер приёма,
    // снизу бледно — тип приёма (если задан). Режим подписи группы здесь не
    // применяется: в схеме столбцы всегда называются «Приём #N».
    // Когда тип не задан, снизу остаётся только метка текущего этапа.
    const headerLabels = (number) => {
        const columnLabel = t("plan.appointment", { number });
        const stage = typeMap[number] || "";
        return {
            primary: columnLabel,
            secondary: stage
                ? stage
                : currentStage === number
                  ? t("plan.matrix.current")
                  : "",
        };
    };

    const cellSummary = (items) => {
        if (!items.length) return t("plan.matrix.emptyCell");
        if (items.length === 1) return displayName(items[0]) || "1";
        return t("plan.matrix.servicesCount", { count: items.length });
    };

    const cellTitle = (items) =>
        items
            .map((item) => displayName(item) || "—")
            .join(", ");

    const openCell = (group, number, items) => {
        setCell({ group, number, items });
    };

    const closeCell = () => setCell(null);

    const cellGroupLabel = (group) =>
        String(group?.name?.value || "").trim()
            ? getGroupTypeLabel(t, group)
            : t("plan.group.noName", { defaultValue: "—" });
    // Кнопка «+ Добавить группу»: в схеме — последняя строка таблицы,
    // при отсутствии групп — отдельная заглушка по центру.
    const addGroupButton =
        onRequestAddGroup && onAddGroup ? (
            <div className="plan-matrix-foot">
                <Button
                    icon="add"
                    onClick={openGroupMenu}
                    size="sm"
                    variant="secondary">
                    {t("plan.buttons.addGroup")}
                </Button>
            </div>
        ) : null;

    if (!groups.length) {
        return (
            <>
                <div className="page-block plan-matrix-empty">
                    <div>{t("plan.matrix.noGroups")}</div>
                    {addGroupButton}
                </div>
                {renderTypeMenu()}
            </>
        );
    }

    return (
        <>
            <div className="card plan-matrix-card">
                <div className="card__head plan-matrix-head">
                    <div>
                        <div className="card__title plan-matrix-title">
                            <IoGridOutline size={19} aria-hidden="true" />
                            {t("plan.matrix.title")}
                        </div>
                        <div className="card__sub plan-matrix-sub">
                            {t("plan.matrix.sub")}
                        </div>
                    </div>
                </div>

                <div className="plan-matrix-wrap">
                    <div
                        className={`plan-matrix${
                            onAddStage ? " plan-matrix--add" : ""
                        }`}
                        style={{ "--plan-stages": stages.length || 1 }}>

                        <div
                            className="plan-matrix__corner"
                            style={{ gridColumn: 1, gridRow: 1 }}>
                            {t("plan.matrix.corner")}
                        </div>

                        {stages.map((number, index) => {
                            const header = headerLabels(number);
                            const isCurrent = currentStage === number;
                            const isOpen =
                                menu?.kind === "stage" &&
                                menu.number === number;
                            // Крестик удаления — у каждого приёма. Удаление
                            // среднего сдвигает последующие номера влево
                            // (делает бэкенд, action `remove_appointment`).
                            const canRemove =
                                Boolean(onRemoveStage) && stages.length > 1;
                            const headerContent = (
                                <>
                                    <span
                                        className="plan-matrix__header-main"
                                        title={header.primary}>
                                        {header.primary}
                                    </span>
                                    {header.secondary ? (
                                        <span
                                            className="plan-matrix__header-sub"
                                            title={header.secondary}>
                                            {header.secondary}
                                        </span>
                                    ) : null}
                                </>
                            );

                            // Кнопка-крестик лежит рядом с шапкой внутри
                            // обёртки (вложенная кнопка в кнопку недопустима).
                            const removeControl = canRemove ? (
                                <button
                                    type="button"
                                    className="plan-matrix__stage-remove"
                                    title={t("plan.matrix.removeStageHint", {
                                        number,
                                    })}
                                    aria-label={t("plan.matrix.removeStage", {
                                        number,
                                    })}
                                    disabled={addingStage}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onRemoveStage(number);
                                    }}>
                                    <IoCloseOutline
                                        size={13}
                                        aria-hidden="true"
                                    />
                                </button>
                            ) : null;

                            const wrapClass = `plan-matrix__stagewrap${
                                canRemove ? " is-removable" : ""
                            }`;

                            if (!onChangeStageType) {
                                return (
                                    <div
                                        key={`header-${number}`}
                                        className={wrapClass}
                                        style={{
                                            gridColumn: index + 2,
                                            gridRow: 1,
                                        }}>
                                        <div
                                            className={`plan-matrix__header${
                                                isCurrent ? " is-current" : ""
                                            }`}>
                                            {headerContent}
                                        </div>
                                        {removeControl}
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={`header-${number}`}
                                    className={wrapClass}
                                    style={{
                                        gridColumn: index + 2,
                                        gridRow: 1,
                                    }}>
                                    <button
                                        type="button"
                                        className={`plan-matrix__header plan-matrix__header--editable${
                                            isCurrent ? " is-current" : ""
                                        }${isOpen ? " is-open" : ""}`}
                                        title={t("plan.matrix.typeMenuHint")}
                                        aria-haspopup="menu"
                                        aria-expanded={isOpen}
                                        onClick={(event) =>
                                            openMenu("stage", number, event)
                                        }>
                                        {headerContent}
                                    </button>
                                    {removeControl}
                                </div>
                            );
                        })}

                        {onAddStage ? (
                            <div
                                className="plan-matrix__addwrap"
                                style={{
                                    gridColumn: stages.length + 2,
                                    gridRow: 1,
                                }}>
                                <button
                                    type="button"
                                    className={`plan-matrix__addstage${
                                        menu?.kind === "add" ? " is-open" : ""
                                    }`}
                                    title={t("plan.matrix.addStageHint")}
                                    aria-haspopup="menu"
                                    aria-expanded={menu?.kind === "add"}
                                    aria-label={t("plan.matrix.addStage")}
                                    onClick={(event) =>
                                        openMenu("add", null, event)
                                    }
                                    disabled={addingStage}>
                                    <IoAddOutline
                                        size={15}
                                        aria-hidden="true"
                                    />
                                    <span className="plan-matrix__addstage-label">
                                        {t("plan.matrix.addStageShort")}
                                    </span>
                                    <IoChevronDownOutline
                                        size={13}
                                        className="plan-matrix__addstage-chev"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        ) : null}

                        {groups.map((group, rowIndex) => (
                            <React.Fragment key={group?.id?.value}>
                                <div
                                    className="plan-matrix__rowlabel"
                                    style={{
                                        gridColumn: 1,
                                        gridRow: rowIndex + 2,
                                    }}>
                                    <IoPulseOutline
                                        size={16}
                                        aria-hidden="true"
                                    />
                                    <span>{cellGroupLabel(group)}</span>
                                </div>
                                {stages.map((number, index) => {
                                    const items = getCellItems(group, number);
                                    const tint = stageState(number);
                                    return (
                                        <button
                                            key={number}
                                            type="button"
                                            className={`plan-matrix__cell is-${tint} is-clickable${
                                                items.length
                                                    ? " is-filled"
                                                    : " is-empty"
                                            }`}
                                            style={{
                                                gridColumn: index + 2,
                                                gridRow: rowIndex + 2,
                                            }}
                                            title={
                                                items.length
                                                    ? cellTitle(items)
                                                    : t("plan.matrix.emptyCell")
                                            }
                                            onClick={() =>
                                                openCell(group, number, items)
                                            }>
                                            {cellSummary(items)}
                                        </button>
                                    );
                                })}
                                {onAddStage ? (
                                    <div
                                        className="plan-matrix__addcell"
                                        aria-hidden="true"
                                        style={{
                                            gridColumn: stages.length + 2,
                                            gridRow: rowIndex + 2,
                                        }}
                                    />
                                ) : null}
                            </React.Fragment>
                        ))}

                        {/* «+ Добавить группу» — последняя строка схемы:
                            ячейка в колонке направлений, продолжение таблицы. */}
                        {onRequestAddGroup && onAddGroup ? (
                            <>
                                <button
                                    type="button"
                                    className={`plan-matrix__addgroup${
                                        menu?.kind === "group" ? " is-open" : ""
                                    }`}
                                    title={t("plan.matrix.groupMenuHint")}
                                    aria-haspopup="menu"
                                    aria-expanded={menu?.kind === "group"}
                                    onClick={openGroupMenu}
                                    style={{
                                        gridColumn: 1,
                                        gridRow: groups.length + 2,
                                    }}>
                                    <IoAddOutline
                                        size={16}
                                        aria-hidden="true"
                                    />
                                    <span>{t("plan.buttons.addGroup")}</span>
                                    <IoChevronDownOutline
                                        size={13}
                                        className="plan-matrix__addgroup-chev"
                                        aria-hidden="true"
                                    />
                                </button>
                                {onAddStage ? (
                                    <div
                                        className="plan-matrix__addcell is-last"
                                        aria-hidden="true"
                                        style={{
                                            gridColumn: stages.length + 2,
                                            gridRow: groups.length + 2,
                                        }}
                                    />
                                ) : null}
                            </>
                        ) : null}
                    </div>
                </div>

                <p className="plan-matrix-note">
                    <IoListOutline size={15} aria-hidden="true" />
                    {t("plan.matrix.note")}
                </p>
            </div>

            {renderTypeMenu()}

            <Modal
                show={Boolean(cell)}
                onHide={() => {
                    setServiceMenuOpen(false);
                    closeCell();
                }}
                size="lg"
                centered
                className="plan-matrix-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t("plan.matrix.modalTitle", {
                            stage: cell ? stageLabel(cell.number) : "",
                        })}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {cell ? (
                        <>
                            <div className="plan-matrix-modal__sub">
                                {cellGroupLabel(cell.group)}
                                {planId
                                    ? ` · ${t("plan.matrix.planNumber", {
                                          id: planId,
                                      })}`
                                    : ""}
                            </div>
                            <ul className="plan-matrix-services">
                                {cell.items.length === 0 ? (
                                    <li className="plan-matrix-services__empty">
                                        {t("plan.matrix.noServicesInStage")}
                                    </li>
                                ) : null}
                                {cell.items.map((item) => {
                                    const appointments = (
                                        item?.appointments || []
                                    ).filter(
                                        (apt) =>
                                            appointmentNumber(apt) ===
                                            cell.number,
                                    );
                                    const appointed = appointments.some(
                                        (apt) => hasAdmission(apt),
                                    );
                                    const comment = String(
                                        appointments[0]?.comment?.value ?? "",
                                    ).trim();

                                    return (
                                        <li
                                            className="plan-matrix-services__item"
                                            key={item?.id?.value}>
                                            <div className="plan-matrix-services__main">
                                                <div className="plan-matrix-services__name">
                                                    {displayName(item) || "—"}
                                                </div>
                                                {item?.composition?.value ? (
                                                    <div className="plan-matrix-services__comp">
                                                        {item.composition.value}
                                                    </div>
                                                ) : null}
                                                {comment ? (
                                                    <div className="plan-matrix-services__comment">
                                                        {comment}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="plan-matrix-services__side">
                                                <span className="plan-matrix-services__price">
                                                    {toNumber(
                                                        item?.amount?.value ??
                                                            item?.amount_base
                                                                ?.value,
                                                    ).toLocaleString(locale)}{" "}
                                                    ₽
                                                </span>
                                                <span
                                                    className={`plan-matrix-services__status${
                                                        appointed
                                                            ? " is-done"
                                                            : ""
                                                    }`}>
                                                    {appointed
                                                        ? t(
                                                              "plan.matrix.statusScheduled",
                                                          )
                                                        : t(
                                                              "plan.matrix.statusPlanned",
                                                          )}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    {onAddService && cell ? (
                        <div className="plan-matrix-modal__addservice">
                            <Button
                                aria-expanded={serviceMenuOpen}
                                aria-haspopup="menu"
                                icon="add"
                                onClick={() =>
                                    setServiceMenuOpen((prev) => !prev)
                                }
                                variant="primary">
                                <span>{t("plan.buttons.addService")}</span>
                                <IoChevronDownOutline
                                    aria-hidden="true"
                                    className={`plan-matrix-modal__addservice-arrow${
                                        serviceMenuOpen ? " is-open" : ""
                                    }`}
                                    size={16}
                                />
                            </Button>
                            {serviceMenuOpen ? (
                                <div
                                    className="plan-matrix-modal__addservice-menu"
                                    role="menu">
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="plan-matrix-modal__addservice-item"
                                        onClick={() => {
                                            const target = cell;
                                            setServiceMenuOpen(false);
                                            closeCell();
                                            onAddService(
                                                target.group,
                                                target.number,
                                            );
                                        }}>
                                        {t(
                                            "plan.buttons.addServiceFromCatalog",
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="plan-matrix-modal__addservice-item"
                                        onClick={() => {
                                            const target = cell;
                                            setServiceMenuOpen(false);
                                            closeCell();
                                            onAddCustomService?.(
                                                target.group,
                                                target.number,
                                            );
                                        }}>
                                        {t("plan.buttons.addServiceNew")}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    <Button onClick={closeCell} variant="secondary">
                        {t("actions.close", { ns: "common" })}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
